import { SILKREELS_API_BASE } from '@/api/config'
import { getActiveTenantCode } from '@/shared/lib/tenant-code'

/** 公开占位 / CDN，浏览器可直接加载，无需 backend 防盗链代理 */
const DIRECT_MEDIA_HOST_SUFFIXES = ['picsum.photos', 'images.unsplash.com', 'placehold.co', 'placekitten.com']

export function isDirectPublicMediaHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/\.$/, '')
  return DIRECT_MEDIA_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`))
}

function isCrossOrigin(url: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    return new URL(url, window.location.href).origin !== window.location.origin
  } catch {
    return true
  }
}

/** 跨域 OSS / CDN 媒体经 backend 公开代理，避免浏览器 Referer 触发防盗链 */
export function shouldProxyMediaUrl(url: string): boolean {
  const raw = url.trim()
  if (!raw || raw.startsWith('blob:') || raw.startsWith('data:')) return false
  try {
    const host = new URL(raw, typeof window !== 'undefined' ? window.location.href : 'http://localhost')
      .hostname
    if (isDirectPublicMediaHost(host)) return false
  } catch {
    /* ignore malformed URL */
  }
  return isCrossOrigin(raw)
}

/** SILKREELS_STORAGE_PUBLIC_URL=/uploads 与资产 URL 再拼接时会出现 /uploads/uploads/。 */
export function collapseDuplicateUploadsPath(url: string): string {
  return url.replace(/\/uploads\/(?:uploads\/)+/gi, '/uploads/')
}

function stripNilPathPrefix(url: string): string {
  let s = url.trim()
  while (s.startsWith('<nil>/')) {
    s = s.slice('<nil>/'.length)
  }
  return collapseDuplicateUploadsPath(s)
}

const VIDEO_MEDIA_PATH = /\.(mp4|webm|mov|m4v|mkv|avi)(\?|#|$)/i

/** cover_url 误存为 mp4 时，<img> 无法渲染（作品广场黑块）。 */
export function isVideoMediaUrl(rawUrl: string | null | undefined): boolean {
  const raw = String(rawUrl ?? '').trim()
  if (!raw) return false
  if (raw.startsWith('data:video/')) return true
  const path = raw.split('?')[0]?.split('#')[0] ?? raw
  if (VIDEO_MEDIA_PATH.test(path)) return true
  try {
    const parsed = new URL(raw, 'http://localhost')
    return VIDEO_MEDIA_PATH.test(parsed.pathname)
  } catch {
    return false
  }
}

/**
 * 本地上传（/uploads）统一走前端同源路径，避免 LAN IP 触发 media proxy 403。
 * 开发态依赖 vite 代理 `/uploads → backend`（与 nginx.conf 一致）；未代理时会落到 SPA HTML，图片空白。
 */
function normalizeBackendUploadPath(url: string): string {
  let raw = stripNilPathPrefix(url)
  if (!raw) return raw
  if (!raw.startsWith('/') && !/^https?:\/\//i.test(raw) && raw.startsWith('uploads/')) {
    raw = `/${raw}`
  }
  if (raw.startsWith('/uploads/')) return raw
  try {
    const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.href : 'http://localhost')
    if (parsed.pathname.startsWith('/uploads/')) {
      return collapseDuplicateUploadsPath(parsed.pathname + parsed.search)
    }
  } catch {
    /* ignore malformed URL */
  }
  return raw
}

/** 首页轮播 / 作品封面 / 个人中心等展示用 URL；配置里仍保存 OSS 直链或 canonical /uploads 路径 */
export function resolveMediaDisplayUrl(rawUrl: string | null | undefined): string {
  const url = normalizeBackendUploadPath(String(rawUrl ?? ''))
  if (!url) return ''
  if (url.startsWith('blob:') || url.startsWith('data:')) return url
  if (/^default\/studio\//.test(url)) return ''

  if (url.startsWith('/uploads/') && !shouldProxyMediaUrl(url)) return url
  if (!/^https?:\/\//i.test(url)) return url
  if (!shouldProxyMediaUrl(url)) return url

  const base = SILKREELS_API_BASE.replace(/\/$/, '')
  const q = new URLSearchParams({ url })
  const tenantCode = getActiveTenantCode()
  if (tenantCode) q.set('tenant', tenantCode)
  const prefix = base || ''
  return `${prefix}/api/v1/public/media/proxy?${q.toString()}`
}
